import { useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils/cn';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { MobileNav } from './MobileNav';
import { ConversationalDrawer } from '../../features/conversational/components/ConversationalDrawer';

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex">
        <Sidebar collapsed={sidebarCollapsed} />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="absolute inset-0 bg-black/30" />
        </div>
      )}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex lg:hidden transition-transform duration-300',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <Sidebar />
        <button
          className="ml-2 mt-4 h-8 w-8 flex items-center justify-center rounded-full bg-white text-slate-600 shadow-md"
          onClick={() => setSidebarOpen(false)}
        >
          ✕
        </button>
      </div>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header onMenuToggle={() => setSidebarOpen(true)} />

        {/* Desktop sidebar collapse button */}
        <button
          onClick={() => setSidebarCollapsed((c) => !c)}
          className="absolute left-0 top-1/2 z-30 hidden lg:flex h-6 w-5 -translate-y-1/2 items-center justify-center rounded-r-md bg-primary-600/20 text-primary-700 hover:bg-primary-600/30 transition-colors"
          title={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
        >
          {sidebarCollapsed ? '›' : '‹'}
        </button>

        <main className="flex-1 overflow-y-auto px-4 py-6 md:px-6 lg:px-8 pb-24 lg:pb-6">
          {children}
        </main>
      </div>

      {/* Mobile bottom nav */}
      <MobileNav />

      {/* Global Conversational Operations Copilot */}
      <ConversationalDrawer />
    </div>
  );
}
